import React, { useState, useEffect, useRef, useCallback } from "react";
import { useVideo } from "../../context/VideoContext";
import "./DelayBox.css";

const DelayBox = () => {
  const {
    subtitle1Delay,
    subtitle2Delay,
    setSubtitle1Delay,
    setSubtitle2Delay,
    showDelayBox,
    setShowDelayBox,
    subtitle1Loaded,
    subtitle2Loaded,
  } = useVideo();

  const [localDelay1, setLocalDelay1] = useState(subtitle1Delay);
  const [localDelay2, setLocalDelay2] = useState(subtitle2Delay);
  const [activeSub, setActiveSub] = useState(null); // برای تشخیص اینکه کدوم زیرنویس در حال تنظیمه
  const timeoutRef = useRef(null);
  const boxRef = useRef(null);

  // همگام‌سازی با مقادیر global
  useEffect(() => {
    setLocalDelay1(subtitle1Delay);
    setLocalDelay2(subtitle2Delay);
  }, [subtitle1Delay, subtitle2Delay]);

  // مدیریت timeout برای بستن خودکار
  useEffect(() => {
    if (showDelayBox) {
      // ریست تایمر قبلی
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // تایمر جدید
      timeoutRef.current = setTimeout(() => {
        setShowDelayBox(false);
        setActiveSub(null);
      }, 3000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [showDelayBox, localDelay1, localDelay2, setShowDelayBox]);

  // تابع افزایش/کاهش تاخیر
  const adjustDelay = (subNumber, delta) => {
    // ریست تایمر
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setShowDelayBox(false);
        setActiveSub(null);
      }, 3000);
    }

    setActiveSub(subNumber);

    if (subNumber === 1) {
      const newDelay = Math.round((localDelay1 + delta) * 10) / 10; // یک رقم اعشار
      setLocalDelay1(newDelay);
      setSubtitle1Delay(newDelay);

      // اعمال تغییر روی زیرنویس
      applyDelayToSubtitle(1, newDelay);
    } else {
      const newDelay = Math.round((localDelay2 + delta) * 10) / 10;
      setLocalDelay2(newDelay);
      setSubtitle2Delay(newDelay);

      // اعمال تغییر روی زیرنویس
      applyDelayToSubtitle(2, newDelay);
    }
  };

  // اعمال تاخیر روی زیرنویس
  const applyDelayToSubtitle = (subNumber, delayMs) => {
    const video = document.querySelector("video");
    if (!video) return;

    const trackIndex = subNumber === 1 ? 0 : 1;
    const textTrack = video.textTracks[trackIndex];

    if (!textTrack || !textTrack.cues) return;

    // محاسبه تاخیر قبلی برای این زیرنویس
    const prevDelay = subNumber === 1 ? subtitle1Delay : subtitle2Delay;
    const deltaDelay = delayMs - prevDelay;

    if (deltaDelay === 0) return;

    // اعمال تغییر روی همه cueها
    Array.from(textTrack.cues).forEach((cue) => {
      cue.startTime += deltaDelay / 1000; // تبدیل به ثانیه
      cue.endTime += deltaDelay / 1000;
    });
  };

  // ریست تاخیر
  const resetDelay = (subNumber) => {
    const video = document.querySelector("video");
    if (!video) return;

    const trackIndex = subNumber === 1 ? 0 : 1;
    const textTrack = video.textTracks[trackIndex];

    if (!textTrack || !textTrack.cues) return;

    const currentDelay = subNumber === 1 ? localDelay1 : localDelay2;

    if (currentDelay !== 0) {
      // برگردوندن همه cueها به حالت اولیه
      Array.from(textTrack.cues).forEach((cue) => {
        cue.startTime -= currentDelay / 1000;
        cue.endTime -= currentDelay / 1000;
      });
    }

    if (subNumber === 1) {
      setLocalDelay1(0);
      setSubtitle1Delay(0);
    } else {
      setLocalDelay2(0);
      setSubtitle2Delay(0);
    }
  };

  // ریست هر دو زیرنویس
  const resetBoth = () => {
    if (subtitle1Loaded) resetDelay(1);
    if (subtitle2Loaded) resetDelay(2);
  };

  const handleBoxClick = useCallback((e) => {
    e.stopPropagation(); // این جلوی انتشار رویداد به والد رو می‌گیره
  }, []);

  // بستن باکس
  const handleClose = () => {
    setShowDelayBox(false);
    setActiveSub(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  if (!showDelayBox) return null;

  return (
    <div className="delay-box" onClick={handleBoxClick} ref={boxRef}>
      <div className="delay-box-header">
        <span>تنظیم تاخیر زیرنویس (میلی‌ثانیه)</span>
        <button className="delay-box-close" onClick={handleClose}>
          <i className="fas fa-times"></i>
        </button>
      </div>

      <div className="delay-box-content">
        {/* زیرنویس اول */}
        {subtitle1Loaded && (
          <div className={`delay-item ${activeSub === 1 ? "active" : ""}`}>
            <div className="delay-item-label">
              <span className="delay-subtitle-badge sub1">زیرنویس ۱</span>
              <span className="delay-value">{localDelay1} ms</span>
            </div>
            <div className="delay-controls">
              <button
                className="delay-btn"
                onMouseDown={() => adjustDelay(1, -100)}
                onTouchStart={() => adjustDelay(1, -100)}
              >
                <i className="fas fa-minus"></i> -۱۰۰
              </button>
              <button
                className="delay-btn"
                onMouseDown={() => adjustDelay(1, -10)}
                onTouchStart={() => adjustDelay(1, -10)}
              >
                -۱۰
              </button>
              <button
                className="delay-btn reset-btn"
                onClick={() => resetDelay(1)}
              >
                <i className="fas fa-undo"></i>
              </button>
              <button
                className="delay-btn"
                onMouseDown={() => adjustDelay(1, 10)}
                onTouchStart={() => adjustDelay(1, 10)}
              >
                +۱۰
              </button>
              <button
                className="delay-btn"
                onMouseDown={() => adjustDelay(1, 100)}
                onTouchStart={() => adjustDelay(1, 100)}
              >
                <i className="fas fa-plus"></i> +۱۰۰
              </button>
            </div>
          </div>
        )}

        {/* زیرنویس دوم */}
        {subtitle2Loaded && (
          <div className={`delay-item ${activeSub === 2 ? "active" : ""}`}>
            <div className="delay-item-label">
              <span className="delay-subtitle-badge sub2">زیرنویس ۲</span>
              <span className="delay-value">{localDelay2} ms</span>
            </div>
            <div className="delay-controls">
              <button
                className="delay-btn"
                onMouseDown={() => adjustDelay(2, -100)}
                onTouchStart={() => adjustDelay(2, -100)}
              >
                <i className="fas fa-minus"></i> -۱۰۰
              </button>
              <button
                className="delay-btn"
                onMouseDown={() => adjustDelay(2, -10)}
                onTouchStart={() => adjustDelay(2, -10)}
              >
                -۱۰
              </button>
              <button
                className="delay-btn reset-btn"
                onClick={() => resetDelay(2)}
              >
                <i className="fas fa-undo"></i>
              </button>
              <button
                className="delay-btn"
                onMouseDown={() => adjustDelay(2, 10)}
                onTouchStart={() => adjustDelay(2, 10)}
              >
                +۱۰
              </button>
              <button
                className="delay-btn"
                onMouseDown={() => adjustDelay(2, 100)}
                onTouchStart={() => adjustDelay(2, 100)}
              >
                <i className="fas fa-plus"></i> +۱۰۰
              </button>
            </div>
          </div>
        )}

        {/* دکمه ریست کلی */}
        {(subtitle1Loaded || subtitle2Loaded) && (
          <div className="delay-reset-all">
            <button className="delay-btn reset-all-btn" onClick={resetBoth}>
              <i className="fas fa-undo-alt"></i> ریست هر دو
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DelayBox;
